import { useEffect, useRef, useState } from 'react';
import { getMetrics } from '../../constants/metricDefinitions';
import './AnalyticsGuide.css';

/**
 * MetricDefinitionsDrawer — slide-in side drawer showing definitions for a set of metrics.
 *
 * Props:
 *   metricKeys  (string[])  – keys of metrics shown on the current page
 *   label       (string)    – trigger button label (default: "Definizioni metriche")
 */
export default function MetricDefinitionsDrawer({ metricKeys = [], label = 'Definizioni metriche' }) {
    const [open, setOpen] = useState(false);
    const drawerRef = useRef(null);
    const metrics = getMetrics(metricKeys);

    // Close on Escape key
    useEffect(() => {
        function onKey(e) {
            if (e.key === 'Escape') setOpen(false);
        }
        if (open) document.addEventListener('keydown', onKey);
        return () => document.removeEventListener('keydown', onKey);
    }, [open]);

    // Close on outside click
    useEffect(() => {
        function onClickOutside(e) {
            if (drawerRef.current && !drawerRef.current.contains(e.target)) {
                setOpen(false);
            }
        }
        if (open) document.addEventListener('mousedown', onClickOutside);
        return () => document.removeEventListener('mousedown', onClickOutside);
    }, [open]);

    if (metrics.length === 0) return null;

    return (
        <>
            <button
                className="metric-drawer-trigger"
                onClick={() => setOpen(true)}
                aria-expanded={open}
            >
                <span className="metric-drawer-trigger-icon">📖</span>
                {label}
            </button>

            {open && <div className="metric-drawer-overlay" aria-hidden="true" />}

            <aside
                ref={drawerRef}
                className={`metric-drawer ${open ? 'metric-drawer--open' : ''}`}
                aria-label="Definizioni metriche"
                role="dialog"
                aria-modal="true"
            >
                <div className="metric-drawer-header">
                    <h2 className="metric-drawer-heading">Definizioni metriche</h2>
                    <button
                        className="metric-drawer-close"
                        onClick={() => setOpen(false)}
                        aria-label="Chiudi"
                    >
                        ✕
                    </button>
                </div>

                <div className="metric-drawer-body">
                    {metrics.map(m => (
                        <div key={m.key} className="metric-drawer-item">
                            <div className="metric-drawer-item-label">{m.label}</div>
                            <div className="metric-drawer-item-definition">{m.shortDefinition}</div>

                            {m.formula && (
                                <div className="metric-drawer-item-row">
                                    <span className="metric-drawer-section-title">Formula</span>
                                    <span>{m.formula}</span>
                                </div>
                            )}

                            {m.sqlQuery && (
                                <div className="metric-drawer-item-row">
                                    <span className="metric-drawer-section-title metric-drawer-section-title--tech">Query DB</span>
                                    <code className="metric-drawer-code">{m.sqlQuery}</code>
                                </div>
                            )}

                            {m.whyItMatters && (
                                <div className="metric-drawer-item-row">
                                    <span className="metric-drawer-section-title">Perché conta</span>
                                    <span>{m.whyItMatters}</span>
                                </div>
                            )}

                            {m.caveat && (
                                <div className="metric-drawer-item-caveat">
                                    <span>⚠</span>
                                    <span>{m.caveat}</span>
                                </div>
                            )}

                            {m.dataSource && (
                                <div className="metric-drawer-item-row metric-drawer-item-source">
                                    <span className="metric-drawer-section-title">Fonte dati</span>
                                    <code className="metric-drawer-code">{m.dataSource}</code>
                                </div>
                            )}
                        </div>
                    ))}
                </div>
            </aside>
        </>
    );
}
